import { useState, useMemo } from 'react';

export const unitOptions = [
  'Seconds',
  'Minutes',
  'Hours',
  'Days',
  'Weeks',
] as const;
export type Unit = (typeof unitOptions)[number];

export const getCronExecutionInterval = (args: {
  unit: Unit;
  frequency: string;
}) => {
  const { unit, frequency } = args;
  let seconds = '00';
  let minutes = '00';
  let hours = '00';
  let days: number | string = '0';

  if (unit === 'Seconds') {
    minutes = Math.floor(+frequency / 60)
      .toString()
      .padStart(2, '0');
    seconds = (+frequency % 60).toString().padStart(2, '0');
  }
  if (unit === 'Minutes') {
    hours = Math.floor(+frequency / 60)
      .toString()
      .padStart(2, '0');
    minutes = (+frequency % 60).toString().padStart(2, '0');
  }
  if (unit === 'Hours') {
    days = Math.floor(+frequency / 24);
    hours = (+frequency % 24).toString().padStart(2, '0');
  }
  if (unit === 'Days') days = frequency || '0';
  if (unit === 'Weeks') days = Number(frequency) * 7;

  return `${days}.${hours}:${minutes}:${seconds}`;
};

export const covertCronToFrequencyAndUnit = (cron: string) => {
  // Handle both formats:
  // 1. "days.hours:minutes:seconds" (e.g., "1.12:30:00")
  // 2. "hours:minutes:seconds" (e.g., "00:05:00")

  if (!cron) {
    return { unit: 'Days' as Unit, frequency: 0 };
  }

  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (cron.includes('.')) {
    // Format with days
    const [daysPart, timePart] = cron.split('.');
    days = +daysPart;
    const [h, m, s] = timePart.split(':').map(Number);
    hours = h;
    minutes = m;
    seconds = s || 0; // Handle case where seconds might be missing
  } else {
    // Format without days (just hours:minutes:seconds)
    const [h, m, s] = cron.split(':').map(Number);
    hours = h;
    minutes = m;
    seconds = s || 0; 
  }

  let unit: Unit;
  let frequency: number;

  if (seconds > 0) {
    unit = 'Seconds';
    if (minutes > 0) frequency = minutes * 60 + seconds;
    else frequency = seconds;
  } else if (minutes > 0) {
    unit = 'Minutes';
    if (hours > 0) frequency = hours * 60 + minutes;
    else frequency = minutes;
  } else if (hours > 0) {
    unit = 'Hours';
    if (days > 0) frequency = days * 24 + hours;
    else frequency = hours;
  } else {
    frequency = days;
    unit = 'Days';
  }

  return { unit, frequency };
};

const useFrequencyByUnit = () => {
  const [frequency, setFrequency] = useState<string>('');
  const [unit, setUnit] = useState<Unit>('Seconds');

  const cronExecutionInterval = useMemo(
    () => getCronExecutionInterval({ unit, frequency }),
    [unit, frequency],
  );

  const cronToFrequencyAndUnit = (cron: string) => {
    const { unit, frequency } = covertCronToFrequencyAndUnit(cron);
    setUnit(unit);
    setFrequency(frequency.toString());
  };

  return {
    frequency,
    setFrequency,
    unit,
    setUnit,
    cronExecutionInterval,
    cronToFrequencyAndUnit,
  };
};

export default useFrequencyByUnit;
